import React from 'react';
import {useParams} from "react-router-dom";
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';
import Button from "@mui/joy/Button";
import Link from '@mui/joy/Link';
import Divider from "@mui/joy/Divider";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import AspectRatio from '@mui/joy/AspectRatio';
import Stack from '@mui/joy/Stack';
import Card from '@mui/joy/Card';
import Chip from '@mui/joy/Chip';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import IconButton from '@mui/material/IconButton';
import DesktopAccessDisabledIcon from '@mui/icons-material/DesktopAccessDisabled';
import StoreIcon from '@mui/icons-material/Store';
import PowerIcon from '@mui/icons-material/Power';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {NotificationService} from "../../../common/notification";
import CircularProgress from "@mui/joy/CircularProgress";
import {AdminUserFullInfo, AdminUserFundsBucketRow, FundsBucketType, FundsBucketTypeName} from "../../../models/user";
import {
    updateUserSteamApiKey,
    updateUserTradeLink,
    userBalanceChangeApi,
    userFullInfoApi,
    userFundsBucketsApi,
    userRemoveBansApi
} from "../usersApi";
import {getAuthMetaDataApi} from "../../login/loginAPI";
import {AuthMetaData} from "../../../models/auth";
import {formatDateTime} from "../../../common/dateTime";
import EditIcon from '@mui/icons-material/Edit';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Checkbox from '@mui/joy/Checkbox';
import ConfirmDialog from "../../../components/confirmDialog/ConfirmDialog";
import {PaymentOperationType} from "../../../models/paymentMethod";
import {formatRubles, rublesToKopecksNumber} from "../../../common/money";
import Table from '@mui/joy/Table';
import Pagination from '@mui/material/Pagination';
import useMediaQuery from '@mui/material/useMediaQuery';
import {PaginationResponse} from "../../../models/paginationResponse";
import { OfferHandleMode, OfferHandleModeDescription } from '../../../models/order';
import {accessBlacklistAddApi, accessBlacklistRemoveUserApi} from '../../accessBlacklist/accessBlacklistApi';
import {AccessBlacklistType} from '../../../models/accessBlacklist';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {UserPromoCodesCard} from './UserPromoCodesCard';

export function UserGeneralInfo({ onAccessBlacklistChanged }: { onAccessBlacklistChanged?: () => void }) {
    const {id} = useParams();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [userInfo, setUserInfo] = React.useState<AdminUserFullInfo | undefined>();
    const [loading, setLoading] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [steamApiKey, setSteamApiKey] = React.useState<string>("");
    const [apiKey, setApiKey] = React.useState<string>("");
    const [tradeLink, setTradeLink] = React.useState<string>("");
    const [originalSteamApiKey, setOriginalSteamApiKey] = React.useState<string>("");
    const [originalTradeLink, setOriginalTradeLink] = React.useState<string>("");
    const [isTokenModalOpen, setIsTokenModalOpen] = React.useState<boolean>(false);
    const [isBalanceModalOpen, setIsBalanceModalOpen] = React.useState<boolean>(false);
    const [blockAccessOpen, setBlockAccessOpen] = React.useState(false);
    const [unblockAccessOpen, setUnblockAccessOpen] = React.useState(false);
    const [blockReason, setBlockReason] = React.useState('');
    const [blockExpires, setBlockExpires] = React.useState('');
    const [blockSaving, setBlockSaving] = React.useState(false);
    const [confirmBalanceChangeOpen, setConfirmBalanceChangeOpen] = React.useState<boolean>(false);
    const [operationType, setOperationType] = React.useState<PaymentOperationType>(PaymentOperationType.Deposit);
    const [amountRub, setAmountRub] = React.useState<string>("");
    const [allowImmediateWithdrawal, setAllowImmediateWithdrawal] = React.useState<boolean>(false);
    const [isWithdrawDetailsOpen, setIsWithdrawDetailsOpen] = React.useState<boolean>(false);
    const [fundsLoading, setFundsLoading] = React.useState<boolean>(false);
    const [fundsPage, setFundsPage] = React.useState<number>(1);
    const [fundsCount, setFundsCount] = React.useState<number>(10);
    const [fundsTotal, setFundsTotal] = React.useState<number>(0);
    const [fundsItems, setFundsItems] = React.useState<AdminUserFundsBucketRow[]>([]);
    const [filterFrom, setFilterFrom] = React.useState<string>("");
    const [filterTo, setFilterTo] = React.useState<string>("");
    const tokenTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    React.useEffect(() => {
        if (id && BigInt(id) > 0) {
            loadUser(BigInt(id)).catch(console.error);
        }
    }, [])

    const loadUser = async (id: bigint) => {
        try {
            setLoading(true);
            let result = await userFullInfoApi(id);
            if (result.result) {
                setUserInfo(result.value);
                const v: any = result.value as any;
                const steamKey = (v?.SteamApiKey ?? v?.steamApiKey ?? "") as string;
                const apiKeyValue = (v?.ApiKey ?? v?.apiKey ?? "") as string;
                const trade = (v?.TradeLink ?? v?.tradeLink ?? "") as string;

                setSteamApiKey(steamKey);
                setApiKey(apiKeyValue);
                setTradeLink(trade);
                setOriginalSteamApiKey(steamKey);
                setOriginalTradeLink(trade);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false);
        }
    }

    const copyTextToClipboard = async (text: string) => {
        if (text == null || text === '') {
            throw new Error('Empty text');
        }
        
        // Используем только современный Clipboard API
        if (navigator.clipboard && window.isSecureContext) {
            try {
                await navigator.clipboard.writeText(text);
                return;
            } catch (clipboardError) {
                console.warn('Clipboard API failed:', clipboardError);
                throw new Error('Не удалось скопировать через Clipboard API. Возможно, требуется HTTPS соединение.');
            }
        }
        
        // Если Clipboard API недоступен (нет secure context), выбрасываем ошибку
        // Пользователю нужно будет скопировать текст вручную
        throw new Error('Clipboard API недоступен. Требуется HTTPS соединение или localhost для автоматического копирования.');
    }

    const loginAsUser = async () => {
        try {
            if (!userInfo){
                 return;
                }
            const sessionToken = userInfo.sessionToken;
            if (!sessionToken) {
                NotificationService.alertError('Отсутствует sessionToken в информации о пользователе');
                return;
            }
            const meta = await getAuthMetaDataApi();
            if (!meta.result) {
                NotificationService.alertError('Не удалось получить метаданные авторизации');
                return;
            }
            const data = meta.value as AuthMetaData;
            const host = data.clientHost;
            if (!host) {
                NotificationService.alertError('Не найден ClientHost в метаданных');
                return;
            }
            const url = `${host}?token=${encodeURIComponent(sessionToken)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            console.error(e);
            NotificationService.alertError('Произошла ошибка при открытии страницы пользователя');
        }
    }

    const removeBan = async (id: bigint) => {
        setLoading(true)
        try {
            let result = await userRemoveBansApi(id);
            if (result.result) {
                NotificationService.alertSuccess("Пометка бана успешно сброшена.")
            } else {
                NotificationService.alertError("Не удалось сбросить пометку бана. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            }
        } finally {
            setLoading(false)
        }

        await loadUser(id);
    }

    const handleBansClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    
    const bansPopperIsOpen = Boolean(anchorEl);
    const popperId = bansPopperIsOpen ? 'bans-popper' : undefined;


    const currentBalanceKopeks = userInfo?.rub ?? 0;
    const withdrawBalanceKopeks = (userInfo as any)?.withdrawRub ?? (userInfo as any)?.WithdrawRub ?? 0;
    const amountKopeks = rublesToKopecksNumber(amountRub);
    const delta = operationType === PaymentOperationType.Deposit ? amountKopeks : -amountKopeks;
    const previewBalance = currentBalanceKopeks + (isNaN(amountKopeks) ? 0 : delta);

    const loadFundsBuckets = async (page?: number, count?: number) => {
        if (!id) return;
        try {
            setFundsLoading(true);
            const fromDate = filterFrom ? new Date(filterFrom) : undefined;
            const toDate = filterTo ? new Date(filterTo) : undefined;
            const resp = await userFundsBucketsApi(BigInt(id), page ?? fundsPage, count ?? fundsCount, fromDate, toDate);
            if (resp.result) {
                const body = resp.value as PaginationResponse<AdminUserFundsBucketRow>;
                setFundsItems(body.items ?? []);
                setFundsTotal(body.total ?? 0);
            } else {
                NotificationService.alertError('Не удалось загрузить детали средств');
            }
        } finally {
            setFundsLoading(false);
        }
    };

    const submitBalanceChange = async () => {
        if (!id || !userInfo) return;
        try {
            setLoading(true);
            const resp = await userBalanceChangeApi(BigInt(id), amountKopeks, operationType, allowImmediateWithdrawal);
            if (resp.result) {
                NotificationService.alertSuccess('Баланс успешно изменён');
                setIsBalanceModalOpen(false);
                setAmountRub("");
                setOperationType(PaymentOperationType.Deposit);
                setAllowImmediateWithdrawal(false);
                await loadUser(BigInt(id));
            } else {
                NotificationService.alertError('Не удалось изменить баланс');
            }
        } finally {
            setLoading(false);
        }
    };

    const Field: React.FC<{ label: string, value: React.ReactNode }> = ({ label, value }) => (
        <>
            <Typography level="body-xs" component="span" sx={{ color: 'text.tertiary' }}>{label}</Typography>
            <Typography level="body-sm" component="div">{value}</Typography>
        </>
    );

    return (
        loading ? <CircularProgress/> :
            <>
                <Card>
                    <Box sx={{mb: 1}}>
                        <Typography level="title-md">Информация</Typography>
                        <Typography level="body-sm">
                            Персональная информация пользователя
                        </Typography>
                    </Box>
                    <Divider/>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 2, sm: 3 }}
                        sx={{ my: 1}}
                        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    >
                        <Stack direction="column"
                               spacing={1}
                               sx={{
                                   justifyContent: "flex-start",
                                   alignItems: "center",
                               }}
                        >
                            <AspectRatio
                                ratio="1"
                                sx={{ maxHeight: { xs: 160, sm: 200 }, minWidth: { xs: 96, sm: 120 }, borderRadius: '100%' }}
                            >
                                <img
                                    src={userInfo?.image}
                                    loading="lazy"
                                    alt=""
                                />
                            </AspectRatio>
                            <Stack
                                direction="row"
                                spacing='1'
                                sx={{
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                {userInfo?.isOnline ?
                                    <Tooltip title='В сети'>
                                        <AspectRatio
                                            ratio="1"
                                            variant="soft"
                                            color="success"
                                            sx={{ minWidth: { xs: 36, sm: 40 }, borderRadius: '50%' }}
                                        >
                                            <div>
                                                <PowerIcon />
                                            </div>
                                        </AspectRatio>
                                    </Tooltip>
                                    :<Tooltip title='Не в сети'>
                                        <AspectRatio
                                            ratio="1"
                                            variant="soft"
                                            color="danger"
                                            sx={{ minWidth: { xs: 36, sm: 40 }, borderRadius: '50%' }}
                                        >
                                            <div>
                                                <DesktopAccessDisabledIcon />
                                            </div>
                                        </AspectRatio>
                                    </Tooltip>
                                }
                                {userInfo?.tradingEnabled ?
                                    <Tooltip title='Торговля активна'>
                                        <AspectRatio
                                            ratio="1"
                                            variant="soft"
                                            color="success"
                                            sx={{ minWidth: { xs: 36, sm: 40 }, borderRadius: '50%' }}
                                        >
                                            <div>
                                                <StoreIcon />
                                            </div>
                                        </AspectRatio>
                                    </Tooltip>
                                    :<></>
                                }
                            </Stack>
                        </Stack>
                        <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{flexGrow: 1}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap'}}>
                                    <Typography level="title-lg">{userInfo?.userName || '—'}</Typography>
                                    {userInfo?.role && (
                                        <Chip size="sm" variant="soft" color="neutral">{userInfo.role}</Chip>
                                    )}
                                    {userInfo?.tradingEnabled && (
                                        <Chip size="sm" variant="soft" color="success">Торговля активна</Chip>
                                    )}
                                    {userInfo?.hasBan && (
                                        <Chip size="sm" variant="soft" color="danger">Есть бан</Chip>
                                    )}
                                    {userInfo?.isAccessBlacklisted && (
                                        <Tooltip
                                            title="Пользователь получает 403 на все запросы в API и ExternalAPI"
                                            variant="outlined"
                                            placement="top"
                                        >
                                            <Chip size="sm" variant="solid" color="danger" sx={{ cursor: 'help' }}>
                                                Доступ заблокирован
                                            </Chip>
                                        </Tooltip>
                                    )}
                                    <Link
                                        underline="hover"
                                        href={`https://steamcommunity.com/profiles/${userInfo?.id.toString()}/`}
                                        target="_blank"
                                        level="body-xs"
                                        sx={{ ml: { xs: 0, sm: 1 } }}
                                    >
                                        Steam ID: {userInfo?.id.toString()}
                                    </Link>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button size="sm" variant="soft" onClick={loginAsUser}>
                                        Войти под юзером
                                    </Button>
                                    <IconButton aria-label="Показать sessionToken" size="small" onClick={() => setIsTokenModalOpen(true)}>
                                        <VisibilityIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                            {userInfo?.hasBan ?
                            <>
                                <Stack direction="row" spacing={1}>
                                    <Button color="danger"
                                            size="sm"
                                            variant="solid"
                                            onClick={handleBansClick}
                                            style={{ maxWidth: 120 }}>
                                        Есть БАН
                                    </Button>
                                    <IconButton aria-label="Снять пометку бана"
                                                color='warning'
                                                onClick={() => removeBan(userInfo?.id)}>
                                        <AspectRatio
                                            ratio="1"
                                            variant="outlined"
                                            color="success"
                                            sx={{ minWidth: 40, borderRadius: '50%' }}
                                        >
                                            <div>
                                                <CleaningServicesIcon />
                                            </div>
                                        </AspectRatio>
                                    </IconButton>
                                </Stack>
                                <Popper id={popperId} open={bansPopperIsOpen} anchorEl={anchorEl}>
                                    <Box sx={{ border: 1, p: 1, bgcolor: 'warning.light', maxWidth: { xs: 320, sm: 400 } }}>
                                        <List dense={true}>
                                        {userInfo?.bans?.map((banInfo) => {
                                            return (
                                                <ListItem>
                                                    <ListItemText primary={banInfo.gameName === undefined
                                                        ? `Steam BAN from ${formatDateTime(banInfo.createdAt)}`
                                                        : `VAC BAN on ${banInfo.gameName} from ${formatDateTime(banInfo.createdAt)}`} />
                                                </ListItem>
                                            );
                                        })}
                                        </List>
                                    </Box>
                                </Popper>
                            </>
                            :<></>}
                            <Divider sx={{ my: 1 }} />
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
                                    columnGap: 2,
                                    rowGap: 1,
                                }}
                            >
                                <Field label="Ник" value={(userInfo?.name ?? '—') + (userInfo?.level != null ? ` (lvl ${userInfo.level})` : '')} />
                                <Field label="Баланс" value={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography level="body-sm">{formatRubles(userInfo?.rub)}</Typography>
                                        <Tooltip title="доступно к выводу" variant="outlined">
                                            <Link
                                                component="button"
                                                onClick={async () => {
                                                    setIsWithdrawDetailsOpen(true);
                                                    setFundsPage(1);
                                                    await loadFundsBuckets(1, fundsCount);
                                                }}
                                                level="body-sm"
                                                underline="hover"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                ({formatRubles(withdrawBalanceKopeks)})
                                            </Link>
                                        </Tooltip>
                                        <IconButton aria-label="Редактировать баланс" size="small" onClick={() => setIsBalanceModalOpen(true)}>
                                            <EditIcon />
                                        </IconButton>
                                    </Stack>
                                } />
                                <Field label="Дата рег." value={formatDateTime(userInfo?.created)} />
                                <Field label="Дата акт." value={formatDateTime(userInfo?.lastActivityAt)} />
                                <Field label="Сумма покупок" value={formatRubles(userInfo?.buyingAmount)} />
                                <Field label="Сумма продаж" value={formatRubles(userInfo?.salesAmount)} />
                                <Field label="Сумма пополнений" value={formatRubles(userInfo?.depositAmount)} />
                                <Field label="Сумма выплат" value={formatRubles(userInfo?.withdrawalAmount)} />
                                <Field label="Скорость доставки, сек." value={new Intl.NumberFormat('ru-RU').format(userInfo?.deliverySpeed ?? 0)} />
                                <Field label="Успешных заказов" value={new Intl.NumberFormat('ru-RU').format(userInfo?.totalSuccess ?? 0)} />
                                <Field label="Неудачных заказов" value={new Intl.NumberFormat('ru-RU').format(userInfo?.totalFailures ?? 0)} />
                                <Field label="Включены звуковые оповещения" value={userInfo?.soundNotification===true?'Да':'Нет'} />
                                <Field label="Включены Web Push" value={userInfo?.webPushNotification===true?'Да':'Нет'} />
                                <Field label="Обработка" value={
                                    userInfo?.offerHandleMode === OfferHandleMode.ByTime
                                        ? userInfo?.offerHandleDelaySeconds
                                            ? `Через ${userInfo?.offerHandleDelaySeconds}сек. после создания`
                                            : OfferHandleModeDescription[OfferHandleMode.Manually]
                                        : OfferHandleModeDescription[userInfo?.offerHandleMode ?? OfferHandleMode.Instant]
                                } />
                                <Field label="Заявки включены" value={userInfo?.bidingEnabled===true?'Да':'Нет'} />
                                <Field label="Офлайн заявки" value={userInfo?.offlineBidsEnabled===true?'Да':'Нет'} />
                                <Field label="Публичный профиль" value={userInfo?.isPublicProfile===true?'Да':'Нет'} />
                            </Box>

                            <Divider sx={{ my: 1 }} />
                            <Typography level="title-sm">Ключи и ссылки</Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '160px 1fr auto' },
                                    columnGap: 2,
                                    rowGap: 1.5,
                                }}
                            >
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Steam API Key</Typography>
                                <Input
                                    size="sm"
                                    value={steamApiKey}
                                    onChange={(e) => setSteamApiKey(e.target.value)}
                                    placeholder="Введите Steam API ключ"
                                />
                                <Button size="sm" variant="solid" onClick={async () => {
                                    if (!id) return;
                                    setLoading(true);
                                    try {
                                        const res = await updateUserSteamApiKey(BigInt(id), steamApiKey);
                                        if (res.result) {
                                            NotificationService.alertSuccess('Steam API ключ сохранён');
                                            setOriginalSteamApiKey(steamApiKey);
                                        } else {
                                            NotificationService.alertError('Не удалось сохранить Steam API ключ');
                                            setSteamApiKey(originalSteamApiKey);
                                        }
                                    } finally {
                                        setLoading(false);
                                    }
                                }}>Сохранить</Button>

                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Trade Link</Typography>
                                <Input
                                    size="sm"
                                    value={tradeLink}
                                    onChange={(e) => setTradeLink(e.target.value)}
                                    placeholder="Введите ссылку обмена"
                                />
                                <Button size="sm" variant="solid" onClick={async () => {
                                    if (!id) return;
                                    setLoading(true);
                                    try {
                                        const res = await updateUserTradeLink(BigInt(id), tradeLink);
                                        if (res.result) {
                                            NotificationService.alertSuccess('Trade link сохранён');
                                            setOriginalTradeLink(tradeLink);
                                        } else {
                                            NotificationService.alertError('Не удалось сохранить trade link');
                                            setTradeLink(originalTradeLink);
                                        }
                                    } finally {
                                        setLoading(false);
                                    }
                                }}>Сохранить</Button>

                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>API Key</Typography>
                                <Input
                                    size="sm"
                                    value={apiKey}
                                    readOnly
                                    variant="soft"
                                    slotProps={{ input: { readOnly: true } }}
                                />
                                <Box />
                                
                            </Box>
                        </Stack>
                    </Stack>

                    {userInfo && (
                        <>
                            <Divider sx={{ mt: 2 }} />
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                                sx={{
                                    mt: 1,
                                    px: userInfo.isAccessBlacklisted ? 1.25 : 0.25,
                                    py: 0.5,
                                    borderRadius: 'sm',
                                    bgcolor: userInfo.isAccessBlacklisted ? 'danger.softBg' : 'transparent',
                                }}
                            >
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                    {userInfo.isAccessBlacklisted && (
                                        <WarningIcon sx={{ fontSize: 16, color: 'danger.500', flexShrink: 0 }} />
                                    )}
                                    <Tooltip
                                        title={
                                            userInfo.isAccessBlacklisted
                                                ? 'Пользователь получает 403 на все запросы в API и ExternalAPI'
                                                : 'Полностью закрывает доступ пользователя к API и ExternalAPI (ответ 403)'
                                        }
                                        variant="outlined"
                                        placement="top"
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.4,
                                                cursor: 'help',
                                                borderBottom: '1px dotted',
                                                borderColor: 'neutral.outlinedBorder',
                                            }}
                                        >
                                            <Typography
                                                level="body-xs"
                                                sx={{
                                                    color: userInfo.isAccessBlacklisted ? 'danger.700' : 'text.tertiary',
                                                }}
                                            >
                                                {userInfo.isAccessBlacklisted
                                                    ? 'Доступ заблокирован'
                                                    : 'Черный список доступа'}
                                            </Typography>
                                            <InfoOutlinedIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                                        </Box>
                                    </Tooltip>
                                </Stack>
                                {userInfo.isAccessBlacklisted ? (
                                    <Button
                                        color="danger"
                                        variant="solid"
                                        size="sm"
                                        onClick={() => setUnblockAccessOpen(true)}
                                    >
                                        Разблокировать
                                    </Button>
                                ) : (
                                    <Button
                                        color="danger"
                                        variant="plain"
                                        size="sm"
                                        onClick={() => setBlockAccessOpen(true)}
                                    >
                                        Заблокировать
                                    </Button>
                                )}
                            </Stack>
                        </>
                    )}

                </Card>

                <UserPromoCodesCard promoCodes={userInfo?.promoCodes} />

                <Modal open={isWithdrawDetailsOpen} onClose={() => setIsWithdrawDetailsOpen(false)}>
                    <ModalDialog 
                        sx={{ 
                            minWidth: { xs: '100%', sm: 600 },
                            maxWidth: { xs: '100%', sm: '100%' },
                            width: { xs: '100%', sm: 'auto' },
                            maxHeight: { xs: '100vh', sm: '90vh' },
                            m: { xs: 0, sm: 'auto' },
                            borderRadius: { xs: 0, sm: 'md' }
                        }}
                    >
                        <Typography level="title-md">Детали доступных средств</Typography>
                        <Box sx={{ 
                            overflowY: 'auto', 
                            maxHeight: { xs: 'calc(100vh - 120px)', sm: 'calc(90vh - 120px)' },
                            mt: 1
                        }}>
                        <Stack spacing={1}>
                            <Stack 
                                direction={{ xs: 'column', sm: 'row' }} 
                                spacing={1} 
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                sx={{ flexWrap: 'wrap' }}
                            >
                                <Chip size="sm" color="danger">Красный — вывод заблокирован</Chip>
                                <Chip size="sm" color="warning">Жёлтый — вывод разрешён</Chip>
                                <Chip size="sm" color="success">Зелёный — всё выведено</Chip>
                            </Stack>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                                sx={{ flexWrap: 'wrap' }}
                            >
                                <FormControl size="sm" sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                                    <FormLabel>С (дата)</FormLabel>
                                    <Input
                                        size="sm"
                                        type="datetime-local"
                                        value={filterFrom}
                                        onChange={(e) => setFilterFrom(e.target.value)}
                                    />
                                </FormControl>
                                <FormControl size="sm" sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                                    <FormLabel>По (дата)</FormLabel>
                                    <Input
                                        size="sm"
                                        type="datetime-local"
                                        value={filterTo}
                                        onChange={(e) => setFilterTo(e.target.value)}
                                    />
                                </FormControl>
                                <Button 
                                    size="sm" 
                                    variant="solid" 
                                    onClick={async () => { setFundsPage(1); await loadFundsBuckets(1, fundsCount); }}
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                >
                                    Применить
                                </Button>
                                <Button 
                                    size="sm" 
                                    variant="soft" 
                                    onClick={async () => {
                                        setFilterFrom(""); setFilterTo(""); setFundsPage(1); await loadFundsBuckets(1, fundsCount);
                                    }}
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                >
                                    Сбросить
                                </Button>
                            </Stack>
                            {isMobile ? (
                                <Stack spacing={1} sx={{ mt: 1 }}>
                                    {fundsLoading ? (
                                        <Typography level="body-sm">Загрузка...</Typography>
                                    ) : (fundsItems.length === 0 ? (
                                        <Typography level="body-sm">Нет данных</Typography>
                                    ) : fundsItems.map((row, idx) => {
                                        const now = new Date();
                                        // Если lockUntil отсутствует - это бесконечная блокировка
                                        const isLocked =
                                            row.type === FundsBucketType.Deposit &&
                                            (!row.lockUntil || new Date(row.lockUntil) > now);
                                        const allWithdrawn = (row.amountUsed ?? 0) >= (row.amountTotal ?? 0);
                                        const color = isLocked ? 'danger' : (allWithdrawn ? 'success' : 'warning');
                                        return (
                                            <Card
                                                key={idx}
                                                variant="outlined"
                                                sx={{
                                                    borderColor: `var(--joy-palette-${color}-outlinedBorder, transparent)`,
                                                    bgcolor: `var(--joy-palette-${color}-softBg, transparent)`
                                                }}
                                            >
                                                <Stack spacing={1}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Дата</Typography>
                                                        <Typography level="body-sm">{formatDateTime(row.created)}</Typography>
                                                    </Stack>
                                                    <Divider />
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Тип операции</Typography>
                                                        <Typography level="body-sm">{FundsBucketTypeName[Number(row.type)] ?? String(row.type)}</Typography>
                                                    </Stack>
                                                    <Divider />
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Сумма</Typography>
                                                        <Typography level="body-sm">{formatRubles(row.amountTotal)}</Typography>
                                                    </Stack>
                                                    <Divider />
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Выведено</Typography>
                                                        <Typography level="body-sm">{formatRubles(row.amountUsed)}</Typography>
                                                    </Stack>
                                                    <Divider />
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Заблокирован до</Typography>
                                                        <Typography level="body-sm">{row.lockUntil ? formatDateTime(row.lockUntil) : row.type === FundsBucketType.Deposit ? 'Бесконечно' : 'Нет блокировки'}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Card>
                                        );
                                    }))}
                                </Stack>
                            ) : (
                                <Table
                                    stickyHeader
                                    variant="outlined"
                                    sx={{ mt: 1, '& thead th': { bgcolor: 'background.level1' } }}
                                >
                                    <thead>
                                    <tr>
                                        <th>Дата</th>
                                        <th>Тип операции</th>
                                        <th>Сумма</th>
                                        <th>Выведено</th>
                                        <th>Заблокирован до</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {fundsLoading ? (
                                        <tr><td colSpan={5}><Typography level="body-sm">Загрузка...</Typography></td></tr>
                                    ) : (fundsItems.length === 0 ? (
                                        <tr><td colSpan={5}><Typography level="body-sm">Нет данных</Typography></td></tr>
                                    ) : fundsItems.map((row, idx) => {
                                        // Если lockUntil отсутствует - это бесконечная блокировка
                                        const isLocked =
                                            row.type === FundsBucketType.Deposit &&
                                            (!row.lockUntil || new Date(row.lockUntil) > new Date());
                                        const allWithdrawn = (row.amountUsed ?? 0) >= (row.amountTotal ?? 0);
                                        const color = isLocked ? 'danger' : (allWithdrawn ? 'success' : 'warning');
                                        return (
                                            <tr key={idx} style={{ backgroundColor: `var(--joy-palette-${color}-softHoverBg, transparent)` }}>
                                                <td>{formatDateTime(row.created)}</td>
                                                <td>{FundsBucketTypeName[Number(row.type)] ?? String(row.type)}</td>
                                                <td>{formatRubles(row.amountTotal)}</td>
                                                <td>{formatRubles(row.amountUsed)}</td>
                                                <td>{row.lockUntil ? formatDateTime(row.lockUntil) : row.type === FundsBucketType.Deposit ? 'Бесконечно' : 'Нет блокировки'}</td>
                                            </tr>
                                        );
                                    }))}
                                    </tbody>
                                </Table>
                            )}
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography level="body-sm">
                                    Всего: {new Intl.NumberFormat('ru-RU').format(fundsTotal)}
                                </Typography>
                                <Pagination
                                    page={fundsPage}
                                    count={Math.max(1, Math.ceil((fundsTotal ?? 0) / fundsCount))}
                                    onChange={async (_e, page) => { setFundsPage(page); await loadFundsBuckets(page, fundsCount); }}
                                    size="small"
                                />
                            </Stack>
                        </Stack>
                        </Box>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button size="sm" variant="soft" onClick={() => setIsWithdrawDetailsOpen(false)}>Закрыть</Button>
                        </Stack>
                    </ModalDialog>
                </Modal>
                <Modal open={isTokenModalOpen} onClose={() => setIsTokenModalOpen(false)}>
                    <ModalDialog>
                        <Typography level="title-md">Session Token</Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>Токен для входа пользователя:</Typography>
                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                            <Textarea
                                size="sm"
                                value={userInfo?.sessionToken ?? ''}
                                readOnly
                                minRows={3}
                                variant="soft"
                                sx={{ minWidth: 360, flexGrow: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                                slotProps={{
                                    textarea: {
                                        ref: tokenTextareaRef,
                                    }
                                }}
                            />
                            <IconButton aria-label="Скопировать токен" size="small" onClick={async () => {
                                try {
                                    await copyTextToClipboard(userInfo?.sessionToken ?? '');
                                    NotificationService.alertSuccess('Токен скопирован');
                                } catch (e) {
                                    // Если Clipboard API недоступен, выделяем текст для ручного копирования
                                    if (tokenTextareaRef.current) {
                                        tokenTextareaRef.current.focus();
                                        tokenTextareaRef.current.select();
                                        // Для мобильных устройств используем setSelectionRange
                                        if (tokenTextareaRef.current.setSelectionRange) {
                                            tokenTextareaRef.current.setSelectionRange(0, tokenTextareaRef.current.value.length);
                                        }
                                        NotificationService.alertError('Автокопирование недоступно. Текст выделен - нажмите Ctrl+C (или Cmd+C на Mac) для копирования.');
                                    } else {
                                        NotificationService.alertError('Не удалось скопировать токен. Скопируйте текст вручную.');
                                    }
                                }
                            }}>
                                <ContentCopyIcon />
                            </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button size="sm" variant="solid" onClick={() => setIsTokenModalOpen(false)}>Закрыть</Button>
                        </Stack>
                    </ModalDialog>
                </Modal>

                <Modal open={isBalanceModalOpen} onClose={() => {
                    setIsBalanceModalOpen(false);
                    setAllowImmediateWithdrawal(false);
                }}>
                    <ModalDialog>
                        <Typography level="title-md">Изменение баланса</Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>Текущий баланс: {formatRubles(currentBalanceKopeks)}</Typography>
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <FormControl size="sm">
                                <FormLabel>Операция</FormLabel>
                                <Select size="sm" value={operationType} onChange={(_, v) => {
                                    setOperationType((v ?? PaymentOperationType.Deposit) as PaymentOperationType);
                                    if (v === PaymentOperationType.Withdraw) {
                                        setAllowImmediateWithdrawal(false);
                                    }
                                }}>
                                    <Option value={PaymentOperationType.Deposit}>Пополнение</Option>
                                    <Option value={PaymentOperationType.Withdraw}>Списание</Option>
                                </Select>
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>Сумма (₽)</FormLabel>
                                <Input size="sm" placeholder="0" value={amountRub} onChange={(e) => setAmountRub(e.target.value)} />
                            </FormControl>
                            {operationType === PaymentOperationType.Deposit && (
                                <FormControl size="sm">
                                    <Checkbox
                                        size="sm"
                                        checked={allowImmediateWithdrawal}
                                        onChange={(e) => setAllowImmediateWithdrawal(e.target.checked)}
                                        label="Разрешить немедленный вывод (депозит не будет заморожен)"
                                    />
                                </FormControl>
                            )}
                            <Typography level="body-sm">Итоговый баланс: {formatRubles(previewBalance)}</Typography>
                            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                                <Button size="sm" variant="soft" onClick={() => {
                                    setIsBalanceModalOpen(false);
                                    setAllowImmediateWithdrawal(false);
                                }}>Отмена</Button>
                                <Button size="sm" variant="solid" disabled={amountKopeks <= 0} onClick={() => setConfirmBalanceChangeOpen(true)}>Изменить</Button>
                            </Stack>
                        </Stack>
                    </ModalDialog>
                </Modal>

                <ConfirmDialog
                    title="Подтверждение изменения баланса"
                    open={confirmBalanceChangeOpen}
                    setOpen={setConfirmBalanceChangeOpen}
                    onConfirm={submitBalanceChange}
                >
                    {`Вы уверены, что хотите ${operationType === PaymentOperationType.Deposit ? 'пополнить' : 'списать'} ${formatRubles(amountKopeks)}?`}
                </ConfirmDialog>

                <Modal open={blockAccessOpen} onClose={() => setBlockAccessOpen(false)}>
                    <ModalDialog>
                        <Typography level="title-md">Заблокировать доступ</Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            Пользователь получит 403 на все запросы в API и ExternalAPI.
                        </Typography>
                        <FormControl sx={{ mt: 1 }}>
                            <FormLabel>Причина</FormLabel>
                            <Textarea minRows={2} value={blockReason} onChange={(e) => setBlockReason(e.target.value)} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Истекает (необязательно)</FormLabel>
                            <Input type="datetime-local" value={blockExpires} onChange={(e) => setBlockExpires(e.target.value)} />
                        </FormControl>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                            <Button variant="plain" onClick={() => setBlockAccessOpen(false)}>Отмена</Button>
                            <Button
                                color="danger"
                                loading={blockSaving}
                                disabled={!blockReason.trim()}
                                onClick={async () => {
                                    if (!id) {
                                        return;
                                    }
                                    setBlockSaving(true);
                                    try {
                                        const res = await accessBlacklistAddApi(
                                            AccessBlacklistType.User,
                                            id,
                                            blockReason,
                                            blockExpires ? new Date(blockExpires).toISOString() : null
                                        );
                                        if (res.result) {
                                            NotificationService.alertSuccess('Пользователь заблокирован: доступ к API закрыт (403)');
                                            setBlockAccessOpen(false);
                                            setBlockReason('');
                                            setBlockExpires('');
                                            await loadUser(BigInt(id));
                                            onAccessBlacklistChanged?.();
                                        } else {
                                            NotificationService.alertError('Не удалось добавить в черный список');
                                        }
                                    } finally {
                                        setBlockSaving(false);
                                    }
                                }}
                            >
                                Заблокировать
                            </Button>
                        </Stack>
                    </ModalDialog>
                </Modal>

                <ConfirmDialog
                    title="Разблокировать доступ"
                    open={unblockAccessOpen}
                    setOpen={setUnblockAccessOpen}
                    onConfirm={async () => {
                        if (!id) {
                            return;
                        }
                        const res = await accessBlacklistRemoveUserApi(BigInt(id));
                        if (res.result) {
                            NotificationService.alertSuccess('Пользователь убран из черного списка');
                            await loadUser(BigInt(id));
                            onAccessBlacklistChanged?.();
                        } else {
                            NotificationService.alertError('Не удалось убрать из черного списка');
                        }
                        setUnblockAccessOpen(false);
                    }}
                >
                    Пользователь снова сможет ходить в API и ExternalAPI. Продолжить?
                </ConfirmDialog>
            </>

    );
}
